'use client';

import * as React from 'react';
import { cx } from '../utils/cx';

interface TabsContextValue {
  baseId: string;
  value: string;
  setValue: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | undefined>(undefined);

function useTabs() {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('Tabs components must be used within Tabs');
  return context;
}

type ControlledTabsProps = {
  value: string;
  onValueChange: (value: string) => void;
  defaultValue?: never;
};

type UncontrolledTabsProps = {
  value?: never;
  onValueChange?: (value: string) => void;
  defaultValue: string;
};

export type TabsProps = (ControlledTabsProps | UncontrolledTabsProps) & {
  children: React.ReactNode;
  className?: string;
};

export function Tabs(props: TabsProps) {
  const { children, className } = props;

  const baseId = React.useId();
  const isControlled = 'value' in props && typeof props.value === 'string';

  const [uncontrolledValue, setUncontrolledValue] = React.useState<string>(() => {
    if (isControlled) return props.value;
    return props.defaultValue;
  });

  const currentValue = isControlled ? props.value : uncontrolledValue;

  const setValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolledValue(next);
      if ('onValueChange' in props) props.onValueChange?.(next);
    },
    [isControlled, props],
  );

  return (
    <TabsContext.Provider value={{ baseId, value: currentValue, setValue }}>
      <div className={cx('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

export function TabsList({ children, className, orientation = 'horizontal' }: TabsListProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    const tabs = Array.from(container.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
    if (tabs.length === 0) return;

    const active = document.activeElement as HTMLElement | null;
    const currentIndex = tabs.findIndex((t) => t === active);

    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';

    const focusAt = (idx: number) => tabs[idx]?.focus();

    if (e.key === prevKey) {
      e.preventDefault();
      focusAt(currentIndex <= 0 ? tabs.length - 1 : currentIndex - 1);
    } else if (e.key === nextKey) {
      e.preventDefault();
      focusAt(currentIndex === -1 || currentIndex >= tabs.length - 1 ? 0 : currentIndex + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAt(tabs.length - 1);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cx(
        'inline-flex h-10 items-center justify-start gap-1 rounded-md p-1',
        'bg-secondary ring-1 ring-secondary_alt ring-inset shadow-xs',
        className,
      )}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export function TabsTrigger({ value, children, disabled = false, className }: TabsTriggerProps) {
  const { baseId, value: selectedValue, setValue } = useTabs();
  const isSelected = selectedValue === value;

  const triggerId = `${baseId}-tab-${value}`;
  const panelId = `${baseId}-panel-${value}`;

  return (
    <button
      id={triggerId}
      type="button"
      role="tab"
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={disabled ? -1 : isSelected ? 0 : -1}
      disabled={disabled}
      onClick={() => {
        if (!disabled) setValue(value);
      }}
      className={cx(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5',
        'text-sm font-medium transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        'disabled:pointer-events-none disabled:opacity-50',
        isSelected
          ? 'bg-primary text-secondary shadow-xs ring-1 ring-secondary_alt ring-inset'
          : 'text-tertiary hover:text-secondary hover:bg-primary_hover',
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { baseId, value: selectedValue } = useTabs();
  if (selectedValue !== value) return null;

  const triggerId = `${baseId}-tab-${value}`;
  const panelId = `${baseId}-panel-${value}`;

  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={triggerId}
      className={cx(
        'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        className,
      )}
    >
      {children}
    </div>
  );
}
